import {filter, flatten} from 'lodash';
import {useCallback, useMemo, useState} from 'react';

import {FormControl, FormControlProps} from '../FormControl';

type Props = {
    value: string;
    name?: string;
    rows?: number;
    maxLength?: number;
    label?: string;
    placeholder?: string;
    textareaClassName?: string;
    styled?: boolean;
    onChange: (value: string) => void;
} & Partial<FormControlProps>;

export type TextAreaProps = Props;

export const TextArea: React.FC<Props> = ({
    name,
    value,
    description,
    label,
    className,
    rows = 3,
    maxLength,
    placeholder,
    textareaClassName,
    required,
    disabled,
    skipErrorMessage,
    hasError,
    errorMessage,
    styled = true,
    spacing,
    onBlur: onBlurProp,
    onChange
}: Props) => {
    const [initialValue] = useState<string>(value);

    const isDirty = useMemo<boolean>(() => value !== initialValue, [initialValue, value]);

    const onBlurHandler = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
        const newValue = e.currentTarget.value;
        onChange(newValue ? newValue.trim() : newValue);
    }, [onChange]);

    const onChangeHandler = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.currentTarget.value;
        onChange(newValue ? newValue.trimStart() : newValue);
    }, [onChange]);

    const mergedDescription = useMemo(() => {
        const maxLengthDescription = maxLength ? `${value?.length || 0} / ${maxLength}` : null;
        return filter(flatten([maxLengthDescription, description]));
    }, [maxLength, value, description]);

    return (
        <FormControl {...{
            name, value, label, description: mergedDescription, className, styled, required, disabled,
            skipErrorMessage, hasError, errorMessage, dirty: isDirty, onBlur: onBlurProp, spacing
        }}
        >
            {({onFocus, onBlur}) => (
                <textarea
                    className={textareaClassName || 'vs-textarea'}
                    rows={rows}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    disabled={disabled}
                    name={name}
                    value={value}
                    onBlur={(e) => {
                        onBlur(e);
                        onBlurHandler(e);
                    }}
                    onFocus={onFocus}
                    onChange={onChangeHandler}
                />
            )}
        </FormControl>
    );
};